import React from 'react';
import PropTypes from 'prop-types';
import Chart from '../Chart/Chart';
import YGrid from '../Chart/YGrid';
import YAxis from '../Chart/YAxis';
import XAxis from '../Chart/XAxis';
import {
        calculateSetDomain,
        calculateSVGXRange,
        calculateSVGYRange,
        calculateChartBoundaries,
        valueToSVGCoordinateMapper,
        } from '../Chart/ChartUtils';
import BoxPlotInsides from './BoxPlotInsides';
import BoxAndWhiskerDataPoint from './BoxAndWhiskerDataPoint';

class BoxPlot extends React.Component {
    static propTypes = {
        containerId: PropTypes.string.isRequired,
        width: PropTypes.number.isRequired,
        height: PropTypes.number.isRequired,
        tooltipFormatFunction: PropTypes.func,
        yValueFormatFunction: PropTypes.func,
        chartSeries: PropTypes.arrayOf(PropTypes.object).isRequired,
        data: PropTypes.arrayOf(PropTypes.instanceOf(BoxAndWhiskerDataPoint)).isRequired,
        whiskerWidth: PropTypes.number.isRequired,
        whiskerColor: PropTypes.string.isRequired ,
        boxWidth: PropTypes.number.isRequired,
        pointRadius: PropTypes.number.isRequired,
        pointOfInterestThickness: PropTypes.number.isRequired,
        margins: PropTypes.shape({
            top: PropTypes.number.isRequired,
            bottom: PropTypes.number.isRequired,
            left: PropTypes.number.isRequired,
            right: PropTypes.number.isRequired,
        }).isRequired,
    }


    state = {
        'tooltip': null,
        'width': 0,
    }

    static defaultProps = {
        tooltipFormatFunction: function(value) {
            return value;
        },
        chartSeries: [
            {
                field: 'min',
                name: 'Min',
            },
            {
                field: 'firstQuartile',
                name: '1st Quartile',
            },
            {
                field: 'median',
                name: 'Median',
            },
            {
                field: 'thirdQuartile',
                name: '3rd Quartile',
            },
            {
                field: 'max',
                name: 'Max',
            },
            {
                field: 'you',
                name: 'You',
            }
        ],
    }

    handleResize = () => {
        const {
            containerId,
        } = this.props;
        let container = document.getElementById(containerId);
        const that = this;

        setTimeout(function() {
            if (container) {
                let containerWidth = container.clientWidth;
                that.setState({
                    width: containerWidth,
                });
            }
        }, 350);

    }

    componentDidMount() {
        this.handleResize();
        window.addEventListener('resize', this.handleResize);
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.handleResize);
    }

    tooltipMouseEnter = (tooltipElement) => {
        this.setState({'tooltip': tooltipElement});
    }

    tooltipMouseLeave = (tooltipElement) => {
        this.setState({'tooltip': null});
    }

    render() {
        const {
            width,
            height,
            containerId,
            tooltipFormatFunction,
            chartSeries,
            data,
            whiskerWidth,
            whiskerColor,
            boxWidth,
            pointRadius,
            pointOfInterestThickness,
            yValueFormatFunction,
            margins,
        } = this.props;

        let scaleFactor = this.state.width / width;
        let scaledWidth = this.state.width;
        let scaledHeight = scaleFactor*height;

        let x = (point) => (point.name);

        let xDomain = calculateSetDomain(data, x)
        let xRange = calculateSVGXRange(scaledWidth, margins);
        let xScaleType = 'band';
        let xValueToSVGCoordinateMapper = valueToSVGCoordinateMapper({
            'scaleType': xScaleType,
            'domain': xDomain,
            'range': xRange,
        });

        let yDomain = [0, data.map((point) => (point.max)).reduce((max, cur) => (Math.max(max, cur)), -Infinity)];
        let yRange = calculateSVGYRange(scaledHeight, margins);
        let yScaleType = 'linear';
        let yValueToSVGCoordinateMapper = valueToSVGCoordinateMapper({
            'scaleType': yScaleType,
            'domain': yDomain,
            'range': yRange,
            'useNiceDomain': true,
        });

        let chartBoundaries = calculateChartBoundaries(scaledWidth, yValueToSVGCoordinateMapper, margins);

        return (
            <div
                style={{
                    width: '100%',
                    position: 'relative'
                }}
                id={containerId}
            >
                <Chart
                    width={scaledWidth}
                    height={scaledHeight}
                    margins={margins}
                >
                    <YGrid
                        chartBoundaries={chartBoundaries}
                        yValueToSVGCoordinateMapper={yValueToSVGCoordinateMapper}
                    />
                    <line
                        className={'box-plot-border'}
                        x2={chartBoundaries.left}
                        y1={chartBoundaries.bottom}
                        y2={chartBoundaries.top}
                        stroke='#000'
                    />
                    <line
                        className={'box-plot-border'}
                        x1={chartBoundaries.right}
                        x2={chartBoundaries.right}
                        y1={chartBoundaries.bottom}
                        y2={chartBoundaries.top}
                        stroke='#000'
                    />
                    <XAxis
                        chartBoundaries={chartBoundaries}
                        xValueToSVGCoordinateMapper={xValueToSVGCoordinateMapper}
                        xTickValues={xDomain}
                    />
                    <YAxis
                        chartBoundaries={chartBoundaries}
                        yValueToSVGCoordinateMapper={yValueToSVGCoordinateMapper}
                        yValueFormatFunction={yValueFormatFunction}
                    />
                    <BoxPlotInsides
                        data={data}
                        whiskerWidth={whiskerWidth * scaleFactor}
                        whiskerColor={whiskerColor}
                        boxWidth={boxWidth * scaleFactor}
                        pointRadius={pointRadius * scaleFactor}
                        pointOfInterestThickness={pointOfInterestThickness * scaleFactor}
                        chartBoundaries={chartBoundaries}
                        chartSeries={chartSeries}
                        tooltipMouseEnter={this.tooltipMouseEnter}
                        tooltipMouseLeave={this.tooltipMouseLeave}
                        tooltipFormatFunction={tooltipFormatFunction}
                        xValueToSVGCoordinateMapper={xValueToSVGCoordinateMapper}
                        yValueToSVGCoordinateMapper={yValueToSVGCoordinateMapper}
                        svgWindowWidth={scaledWidth}
                        chartBoundaries={chartBoundaries}
                        margins={margins}
                        x={x}
                    />
                </Chart>
                { this.state.tooltip }
            </div>
        );
    }
};

export default BoxPlot;
